/**
 * PIXEL ICONS — little 1-bit icons in the style of the early Macintosh
 * ---------------------------------------------------------------------------
 * Each icon is drawn on a 16 × 16 grid, one string per row:
 *   "#"  black pixel
 *   "."  white pixel (fills, so the icon stays readable over text)
 *   " "  transparent
 *
 * To add an icon, add a new entry below and use its name, e.g.
 *   <a href="…" data-cursor-icon="mail">…</a>
 * (see components/CursorIcon.js).
 */
export const ICONS = {
  // Envelope — for email links
  mail: [
    "                ",
    "                ",
    "################",
    "##............##",
    "#.#..........#.#",
    "#..#........#..#",
    "#...#......#...#",
    "#....#....#....#",
    "#.....#..#.....#",
    "#......##......#",
    "#..............#",
    "#..............#",
    "#..............#",
    "################",
    "                ",
    "                ",
  ],

  // Two chain links — for web links
  link: [
    "                ",
    "                ",
    "                ",
    "                ",
    " ########       ",
    "#        #      ",
    "#      # # #### ",
    "#     #  #     #",
    "#     #  #     #",
    "#     #  #     #",
    "#     #  #     #",
    " #### # #      #",
    "      #        #",
    "       ######## ",
    "                ",
    "                ",
  ],

  // Document with a folded corner — for files (CV, PDFs)
  document: [
    "  #########     ",
    "  #.......##    ",
    "  #.......#.#   ",
    "  #.......#..#  ",
    "  #.......####  ",
    "  #..........#  ",
    "  #..........#  ",
    "  #..........#  ",
    "  #..........#  ",
    "  #..........#  ",
    "  #..........#  ",
    "  #..........#  ",
    "  #..........#  ",
    "  #..........#  ",
    "  #..........#  ",
    "  ############  ",
  ],
};

/**
 * Draws one of the ICONS as crisp square pixels.
 * @param {object} props
 * @param {string} props.name    Key in ICONS.
 * @param {number} [props.size]  Width/height on screen in px (default 32,
 *                               i.e. each grid pixel is 2 × 2 screen px).
 */
export default function PixelIcon({ name, size = 32 }) {
  const rows = ICONS[name];
  if (!rows) return null;

  // Merge each run of same-coloured pixels in a row into one rectangle
  const rects = [];
  rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const c = row[x];
      let end = x;
      while (end < row.length && row[end] === c) end++;
      if (c !== " ") {
        rects.push(
          <rect
            key={`${x}-${y}`}
            x={x}
            y={y}
            width={end - x}
            height={1}
            fill={c === "#" ? "#000" : "#fff"}
          />
        );
      }
      x = end;
    }
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      {rects}
    </svg>
  );
}
